/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.auth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.ui.CauceButton
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.auth.generated.resources.Res
import studio.oddpods.cauce.feature.auth.generated.resources.otp_change_email
import studio.oddpods.cauce.feature.auth.generated.resources.otp_code_label
import studio.oddpods.cauce.feature.auth.generated.resources.otp_resend
import studio.oddpods.cauce.feature.auth.generated.resources.otp_subtitle
import studio.oddpods.cauce.feature.auth.generated.resources.otp_title
import studio.oddpods.cauce.feature.auth.generated.resources.otp_verify

@Composable
fun OtpScreen(
    email: String,
    onAuthSuccess: () -> Unit,
    onNavigateBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: OtpViewModel = koinViewModel { parametersOf(email) },
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            OtpEvent.AuthSuccess -> onAuthSuccess()
            OtpEvent.NavigateBackToLanding -> onNavigateBack()
        }
    }

    OtpScreenContent(
        state = state,
        onCodeChange = { viewModel.trySendAction(OtpAction.CodeChanged(it)) },
        onVerifyClick = { viewModel.trySendAction(OtpAction.VerifyClicked) },
        onResendClick = { viewModel.trySendAction(OtpAction.ResendClicked) },
        onChangeEmailClick = { viewModel.trySendAction(OtpAction.ChangeEmailClicked) },
        modifier = modifier,
    )
}

@Composable
internal fun OtpScreenContent(
    state: OtpState,
    onCodeChange: (String) -> Unit,
    onVerifyClick: () -> Unit,
    onResendClick: () -> Unit,
    onChangeEmailClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    KptScaffold(modifier = modifier) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = 480.dp)
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = stringResource(Res.string.otp_title),
                    style = MaterialTheme.typography.headlineMedium,
                )
                Spacer(Modifier.height(8.dp))
                Text(
                    text = stringResource(Res.string.otp_subtitle, state.email),
                    style = MaterialTheme.typography.bodyMedium,
                )

                Spacer(Modifier.height(32.dp))

                OutlinedTextField(
                    value = state.code,
                    onValueChange = onCodeChange,
                    label = { Text(stringResource(Res.string.otp_code_label)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                    singleLine = true,
                    isError = state.error != null,
                    modifier = Modifier.fillMaxWidth(),
                )

                if (state.error != null) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = state.error.asString(),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall,
                    )
                }

                Spacer(Modifier.height(24.dp))

                CauceButton(
                    text = stringResource(Res.string.otp_verify),
                    onClick = onVerifyClick,
                    isLoading = state.isLoading,
                    enabled = state.code.length == 6,
                    modifier = Modifier.fillMaxWidth(),
                )

                Spacer(Modifier.height(16.dp))

                TextButton(
                    onClick = onResendClick,
                    enabled = !state.isLoading,
                ) {
                    Text(stringResource(Res.string.otp_resend))
                }

                TextButton(onClick = onChangeEmailClick) {
                    Text(stringResource(Res.string.otp_change_email))
                }
            }
        }
    }
}
